import { Helmet } from "react-helmet-async";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  getBrandDescription,
  getBrandTitle,
  getOgImageUrl,
  PRODUCT_BRAND,
  SITE_ORIGINS,
} from "@/config/platform";
import { isPortugalPtCanonicalPath, resolveLocalizedPaths } from "@/config/destinationRoutes";
import { isEnHostname, isPtHostname } from "@/config/domains";
import { SEO_CONFIG } from "@/utils/seo";
import { alignStructuredDataBreadcrumbs } from "@/utils/structuredData";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  /** Override canonical path (e.g. legacy /visto-d6 → /destinations/portugal/visto-d6) */
  canonicalPath?: string;
  type?: "website" | "article" | "product";
  structuredData?: object | object[];
  noIndex?: boolean;
}

// URL prefixes that belong to the PT domain (denvoramobility.pt).
// When served on .com, canonical must point to .pt so Google does not treat them as duplicate EN content.
const PT_PATH_PREFIXES = [
  '/visto-',
  '/saude',
  '/consultoria-legal',
  '/todos-os-vistos',
  '/imobiliario',
  '/negocios',
  '/contato',
  '/sobre-nos',
  '/guias-emigrantes',
  '/incentivos',
  '/parceiros',
  '/financiamento',
  '/politica-privacidade',
  '/termos-uso',
  '/rgpd',
  // /interactive is bilingual (same path on .com and .pt) — canonical stays on serving domain
];

// EN paths that share a prefix with PT_PATH_PREFIXES — must NOT be treated as PT
const PT_PATH_EXCEPTIONS = ['/startup-visa-portugal-en'];

const isPortuguesePath = (path: string): boolean => {
  if (isPortugalPtCanonicalPath(path)) return true;
  if (PT_PATH_EXCEPTIONS.some((p) => path.startsWith(p))) return false;
  if (path.startsWith('/startup-visa-portugal') && !path.startsWith('/startup-visa-portugal-en')) return true;
  return PT_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
};

const SEO = ({ 
  title, 
  description, 
  keywords,
  image = getOgImageUrl("pt"),
  url,
  canonicalPath,
  type = "website",
  structuredData,
  noIndex = false
}: SEOProps) => {
  const { language } = useLanguage();

  // Get current path without domain
  const currentPath = url || '/';

  // On .com: PT-language paths are canonical on .pt.
  const canonicalPathResolved = canonicalPath ?? currentPath;

  const getCanonicalBaseUrl = (): string => {
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      if (isPtHostname(hostname)) return SITE_ORIGINS.pt;
      if (isEnHostname(hostname)) {
        return isPortuguesePath(canonicalPathResolved) ? SITE_ORIGINS.pt : SITE_ORIGINS.en;
      }
    }
    return language === 'en' ? SITE_ORIGINS.en : SITE_ORIGINS.pt;
  };
  
  const canonicalUrl = canonicalPathResolved
    ? `${getCanonicalBaseUrl()}${canonicalPathResolved}`
    : getCanonicalBaseUrl();

  // Hreflang: resolve localized slugs (canonicalPath → live pathname → legacy url prop).
  const hreflangLookupPath =
    canonicalPath ??
    (typeof window !== "undefined" ? window.location.pathname : undefined) ??
    currentPath;
  const localizedPaths = resolveLocalizedPaths(hreflangLookupPath);

  const getAlternateUrl = (lang: "pt" | "en"): string => {
    const domain = lang === "pt" ? SITE_ORIGINS.pt : SITE_ORIGINS.en;
    const path = localizedPaths[lang];
    return `${domain}${path}`;
  };
  
  const defaultTitle = getBrandTitle(language);
  const defaultDescription = getBrandDescription(language);

  const seoTitle = title || defaultTitle;
  const seoDescription = description || defaultDescription;
  
  // Ensure title is under 60 characters
  const optimizedTitle = seoTitle.length > 60 ? seoTitle.substring(0, 57) + "..." : seoTitle;
  
  // Ensure description is under 160 characters
  const optimizedDescription = seoDescription.length > 160 ? seoDescription.substring(0, 157) + "..." : seoDescription;

  const structuredDataForPage = alignStructuredDataBreadcrumbs(structuredData, canonicalUrl);

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{optimizedTitle}</title>
      <meta name="description" content={optimizedDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={noIndex ? "noindex, nofollow" : "index, follow"} />
      <meta name="author" content={PRODUCT_BRAND} />
      {/* viewport stays in index.html only — avoids duplicate <meta name="viewport"> with Helmet */}
      <meta httpEquiv="Content-Language" content={language === 'pt' ? 'pt-PT' : 'en-GB'} />
      <meta name="geo.region" content={language === 'pt' ? 'PT-11' : 'PT-11'} />
      <meta name="geo.placename" content="Lisboa" />
      <meta name="geo.position" content="38.7223;-9.1393" />
      <meta name="ICBM" content="38.7223, -9.1393" />
      <meta name="language" content={language === 'pt' ? 'Portuguese' : 'English'} />
      <meta name="revisit-after" content="7 days" />
      <meta name="distribution" content="global" />
      <meta name="rating" content="general" />
      
      {/* Canonical URL — PT paths on denvoramobility.com point to denvoramobility.pt */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Hreflang for internationalization - x-default = EN (.com) as fallback for unspecified regions */}
      <link rel="alternate" href={getAlternateUrl("pt")} hrefLang="pt-PT" />
      <link rel="alternate" href={getAlternateUrl("en")} hrefLang="en-GB" />
      <link rel="alternate" href={getAlternateUrl("en")} hrefLang="x-default" />
      
      {/* Open Graph Tags */}
      <meta property="og:title" content={optimizedTitle} />
      <meta property="og:description" content={optimizedDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={PRODUCT_BRAND} />
      <meta property="og:locale" content={language === 'pt' ? 'pt_PT' : 'en_GB'} />
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={optimizedTitle} />
      <meta name="twitter:description" content={optimizedDescription} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:site" content={SEO_CONFIG.twitterHandle} />
      
      {/* Structured Data */}
      {structuredDataForPage && (
        Array.isArray(structuredDataForPage) ? (
          structuredDataForPage.map((schema, index) => (
            <script key={index} type="application/ld+json">
              {JSON.stringify(schema)}
            </script>
          ))
        ) : (
          <script type="application/ld+json">
            {JSON.stringify(structuredDataForPage)}
          </script>
        )
      )}
    </Helmet>
  );
};

export default SEO;